import { StyleSheet, View } from 'react-native';
import FastImage from '@d11/react-native-fast-image';
import { mvs } from 'react-native-size-matters';

import { HTMLContentRenderer } from './HTMLContentRenderer';
import { PodcastMeta } from './PodcastMeta';
import { useAppSelector } from '../../app/hooks';
import { COLORS } from '../../constants';
import {
  selectDefaultCover,
  selectDivRadius,
} from '../../features/appconfig/store/selectors';
import { useAdjustedFontSize } from '../../hooks/useAdjustedFontSize';
import useOrientation from '../../hooks/useOrientation';
import { PodcastModel } from '../../models';
import CustomText from '../CustomText';

interface PodcastHeaderTabletProps {
  podcastContent: PodcastModel | null;
}

export function PodcastHeaderTablet({ podcastContent }: PodcastHeaderTabletProps) {
  const divRadius = useAppSelector(selectDivRadius);
  const defaultCover = useAppSelector(selectDefaultCover);

  // dynamic font sizes for tablet
  const podcastTitleFontSize = useAdjustedFontSize(mvs(32));

  // portrait specific styles
  const orientation = useOrientation();
  const isPortrait = orientation === 'PORTRAIT';

  return (
    <>
      <View style={styles.podcastHeader}>
        {/* podcast thumbnail */}
        <View style={[styles.thumbnailContainer, { borderRadius: divRadius }]}>
          <FastImage
            source={podcastContent?.logo ? { uri: podcastContent?.logo } : defaultCover}
            style={styles.thumbnail}
            resizeMode={FastImage.resizeMode.cover}
          />
        </View>

        {/* podcast info */}
        <View style={styles.podcastInfo}>
          {/* podcast title */}
          <CustomText
            style={[
              styles.podcastTitle,
              {
                fontSize: podcastTitleFontSize,
              },
            ]}
            weight="Bold"
            numberOfLines={2}
          >
            {podcastContent?.title || ''}
          </CustomText>

          {/* podcast description */}
          <View style={styles.podcastDescriptionContainer}>
            <HTMLContentRenderer
              textColor={COLORS.WHITE}
              opacity={0.7}
              maxCharacters={isPortrait ? 150 : 300}
              content={podcastContent?.description || ''}
            />
          </View>

          {/* podcast meta info */}
          {!isPortrait && <PodcastMeta podcast={podcastContent} />}
        </View>
      </View>
      <View style={styles.podcastMetaPortraitContainer}>
        {isPortrait && <PodcastMeta podcast={podcastContent} />}
      </View>
    </>
  );
}

const styles = StyleSheet.create({
  podcastHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: mvs(16),
    gap: mvs(24),
  },
  thumbnailContainer: {
    width: '30%',
    aspectRatio: 1,
    overflow: 'hidden',
    flexShrink: 0,
  },
  thumbnail: {
    width: '100%',
    height: '100%',
  },
  podcastInfo: {
    flex: 1,
    alignItems: 'flex-start',
  },
  podcastDescriptionContainer: {
    marginBottom: mvs(32),
  },
  podcastTitle: {
    textAlign: 'left',
    fontWeight: 'bold',
    marginBottom: mvs(4),
    color: COLORS.WHITE,
  },
  podcastMetaPortraitContainer: {
    marginBottom: mvs(12),
  },
});
